import React, { useState } from 'react';
import {
  Megaphone,
  Plus,
  Edit2,
  Trash2,
  Copy,
  CheckCircle,
  AlertTriangle,
  Info,
  ShieldAlert,
  Sparkles,
  Eye,
  Calendar,
  Clock,
  Users,
  Target,
  Layers,
  ArrowUp,
  ArrowDown,
  X,
  Check,
  Search,
  ExternalLink,
  ChevronRight,
  Loader2,
  Sliders,
  Palette,
  Layout,
  RefreshCw
} from 'lucide-react';
import {
  AnnouncementBanner,
  FeatureHighlightItem,
  BannerTone,
  BannerDisplayMode,
  UserRole
} from '../types.ts';
import { DynamicIcon, IconPicker } from './DynamicIcon.tsx';
import BannerBar from './BannerBar.tsx';
import FeatureShowcaseModal from './FeatureShowcaseModal.tsx';

interface AnnouncementManagerProps {
  announcements: AnnouncementBanner[];
  onSave: (announcement: AnnouncementBanner) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onToggleActive?: (id: string, active: boolean) => Promise<void>;
  currentUserEmail?: string | null;
  onRefresh?: () => void;
}

const ALL_ROLES: { id: UserRole; label: string }[] = [
  { id: 'admin', label: 'Administrator' },
  { id: 'editor', label: 'CRM Editor' },
  { id: 'sales', label: 'Sales Agent' },
  { id: 'warehouse', label: 'Warehouse Agent' },
];

const STANDARD_DEPARTMENTS = [
  'Sales',
  'Operations',
  'Dispatch',
  'Accounting & Billing',
  'Executive',
  'Customer Service',
  'IT & Systems'
];

const TONE_OPTIONS: { id: BannerTone; label: string; desc: string; badgeClass: string }[] = [
  { id: 'feature', label: 'Feature (Spotlight)', desc: 'Indigo/Purple gradient with star shimmer', badgeClass: 'bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 border-indigo-500/30' },
  { id: 'info', label: 'Info (Standard)', desc: 'Clean blue tone for general updates', badgeClass: 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30' },
  { id: 'success', label: 'Success (Resolved)', desc: 'Emerald green for milestones and fixes', badgeClass: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/30' },
  { id: 'warning', label: 'Warning (Notice)', desc: 'Amber tone for upcoming maintenance', badgeClass: 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30' },
  { id: 'alert', label: 'Alert (Urgent)', desc: 'Rose red for urgent operational disruptions', badgeClass: 'bg-rose-500/20 text-rose-600 dark:text-rose-400 border-rose-500/30' },
];

const DISPLAY_MODES: { id: BannerDisplayMode; label: string; desc: string }[] = [
  { id: 'both', label: 'Top Banner + Modal Showcase', desc: 'Shows sticky banner & launches modal on first visit/click' },
  { id: 'top_banner', label: 'Top Banner Only', desc: 'Displays only the top sticky announcement bar' },
  { id: 'modal_showcase', label: 'Modal Showcase Only', desc: 'Displays in "What’s New" modal & header trigger' },
];

const DEFAULT_FEATURE_ITEM: FeatureHighlightItem = {
  id: '',
  iconName: 'Sparkles',
  title: '',
  description: '',
  badge: 'NEW',
  actionLabel: '',
  actionUrl: ''
};

export default function AnnouncementManager({
  announcements,
  onSave,
  onDelete,
  onToggleActive,
  currentUserEmail,
  onRefresh
}: AnnouncementManagerProps) {
  const [activeView, setActiveView] = useState<'list' | 'editor'>('list');
  const [editingBanner, setEditingBanner] = useState<AnnouncementBanner | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  // Live preview mode: 'banner' or 'modal'
  const [previewTab, setPreviewTab] = useState<'banner' | 'modal'>('banner');
  const [isLiveModalPreviewOpen, setIsLiveModalPreviewOpen] = useState(false);

  // Audience targeting mode in editor
  const [targetingMode, setTargetingMode] = useState<'all' | 'custom'>('all');
  const [emailInput, setEmailInput] = useState('');

  // Start new announcement
  const handleCreateNew = () => {
    const newBanner: AnnouncementBanner = {
      id: `ann-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      title: '',
      message: '',
      tone: 'feature',
      displayMode: 'both',
      isActive: true,
      targetRoles: [],
      targetDepartmentIds: [],
      targetDepartmentNames: [],
      targetUserEmails: [],
      featureItems: [
        {
          id: `item-${Date.now()}-1`,
          iconName: 'Zap',
          title: 'New Feature Highlight',
          description: 'Describe the newly launched capability, rate engine improvement, or workflow shortcut.',
          badge: 'NEW',
          actionLabel: 'Explore Feature',
          actionUrl: 'dashboard'
        }
      ],
      ctaLabel: "See What's New",
      ctaAction: 'open_modal',
      dismissible: true,
      expiresAt: '',
      createdAt: new Date().toISOString(),
      createdBy: currentUserEmail || 'msterling@armellini.com',
      createdByName: 'Administrator',
      dismissedBy: []
    };

    setEditingBanner(newBanner);
    setTargetingMode('all');
    setSaveError(null);
    setSaveSuccess(null);
    setActiveView('editor');
  };

  const handleEdit = (ann: AnnouncementBanner) => {
    setEditingBanner({ ...ann, featureItems: ann.featureItems ? [...ann.featureItems] : [] });
    const hasCustomTargeting =
      (ann.targetRoles && ann.targetRoles.length > 0) ||
      (ann.targetDepartmentNames && ann.targetDepartmentNames.length > 0) ||
      (ann.targetUserEmails && ann.targetUserEmails.length > 0);
    setTargetingMode(hasCustomTargeting ? 'custom' : 'all');
    setSaveError(null);
    setSaveSuccess(null);
    setActiveView('editor');
  };

  const handleDuplicate = (ann: AnnouncementBanner) => {
    const duplicated: AnnouncementBanner = {
      ...ann,
      id: `ann-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      title: `${ann.title} (Copy)`,
      createdAt: new Date().toISOString(),
      dismissedBy: []
    };
    setEditingBanner(duplicated);
    const hasCustomTargeting =
      (duplicated.targetRoles && duplicated.targetRoles.length > 0) ||
      (duplicated.targetDepartmentNames && duplicated.targetDepartmentNames.length > 0) ||
      (duplicated.targetUserEmails && duplicated.targetUserEmails.length > 0);
    setTargetingMode(hasCustomTargeting ? 'custom' : 'all');
    setSaveError(null);
    setSaveSuccess(null);
    setActiveView('editor');
  };

  const handleSaveEditor = async () => {
    if (!editingBanner) return;
    if (!editingBanner.title.trim() || !editingBanner.message.trim()) {
      setSaveError('Title and message are required.');
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      const bannerToSave: AnnouncementBanner = {
        ...editingBanner,
        targetRoles: targetingMode === 'all' ? [] : (editingBanner.targetRoles || []),
        targetDepartmentNames: targetingMode === 'all' ? [] : (editingBanner.targetDepartmentNames || []),
        targetUserEmails: targetingMode === 'all' ? [] : (editingBanner.targetUserEmails || []),
      };

      await onSave(bannerToSave);
      setSaveSuccess('Announcement saved successfully.');
      setTimeout(() => {
        setActiveView('list');
      }, 600);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save announcement');
    } finally {
      setIsSaving(false);
    }
  };

  // Feature Items Helpers
  const handleAddFeatureItem = () => {
    if (!editingBanner) return;
    const newItem: FeatureHighlightItem = {
      id: `item-${Date.now()}-${Math.random().toString(36).substring(2, 5)}`,
      iconName: 'Sparkles',
      title: 'New Feature Highlight',
      description: 'Describe the value proposition or instructions for users.',
      badge: 'NEW',
      actionLabel: '',
      actionUrl: ''
    };
    setEditingBanner({
      ...editingBanner,
      featureItems: [...(editingBanner.featureItems || []), newItem]
    });
  };

  const handleUpdateFeatureItem = (index: number, updates: Partial<FeatureHighlightItem>) => {
    if (!editingBanner || !editingBanner.featureItems) return;
    const updatedItems = [...editingBanner.featureItems];
    updatedItems[index] = { ...updatedItems[index], ...updates };
    setEditingBanner({ ...editingBanner, featureItems: updatedItems });
  };

  const handleDeleteFeatureItem = (index: number) => {
    if (!editingBanner || !editingBanner.featureItems) return;
    const updatedItems = editingBanner.featureItems.filter((_, i) => i !== index);
    setEditingBanner({ ...editingBanner, featureItems: updatedItems });
  };

  const handleMoveFeatureItem = (index: number, direction: 'up' | 'down') => {
    if (!editingBanner || !editingBanner.featureItems) return;
    const items = [...editingBanner.featureItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    const temp = items[index];
    items[index] = items[targetIndex];
    items[targetIndex] = temp;
    setEditingBanner({ ...editingBanner, featureItems: items });
  };

  // Email Whitelist Tag Handling
  const handleAddEmailTag = () => {
    if (!emailInput.trim() || !editingBanner) return;
    const email = emailInput.trim().toLowerCase();
    const current = editingBanner.targetUserEmails || [];
    if (!current.includes(email)) {
      setEditingBanner({
        ...editingBanner,
        targetUserEmails: [...current, email]
      });
    }
    setEmailInput('');
  };

  const handleRemoveEmailTag = (email: string) => {
    if (!editingBanner) return;
    setEditingBanner({
      ...editingBanner,
      targetUserEmails: (editingBanner.targetUserEmails || []).filter(e => e !== email)
    });
  };

  // Filtered announcements list
  const filteredList = announcements.filter(a =>
    !searchFilter ||
    a.title.toLowerCase().includes(searchFilter.toLowerCase()) ||
    a.message.toLowerCase().includes(searchFilter.toLowerCase()) ||
    a.tone.toLowerCase().includes(searchFilter.toLowerCase())
  );

  return (
    <div className="space-y-6">
      
      {/* Module Title Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-4">
        <div>
          <h2 className="text-lg font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            Announcement Banners & "What's New" Feature Spotlight
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Publish top sticky announcement banners, release highlights modals, and audience-targeted notifications.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-2 bg-slate-100 dark:bg-slate-900 hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-lg text-xs font-bold transition cursor-pointer border border-slate-200 dark:border-slate-800"
              title="Refresh Announcements"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}

          {activeView === 'list' ? (
            <button
              onClick={handleCreateNew}
              className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-extrabold transition cursor-pointer flex items-center gap-1.5 shadow-sm active:scale-[0.98]"
            >
              <Plus className="w-4 h-4" />
              <span>Create Announcement</span>
            </button>
          ) : (
            <button
              onClick={() => setActiveView('list')}
              className="px-3.5 py-2 bg-slate-100 dark:bg-slate-900 hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-bold transition cursor-pointer border border-slate-200 dark:border-slate-800"
            >
              Back to List
            </button>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 1. LIST VIEW */}
      {/* ========================================================================= */}
      {activeView === 'list' && (
        <div className="space-y-4">
          
          {/* Metrics summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 font-mono">Total Announcements</span>
              <p className="text-xl font-black text-slate-900 dark:text-slate-100 mt-0.5">{announcements.length}</p>
            </div>
            <div className="p-3.5 rounded-xl bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 font-mono">Live Active</span>
              <p className="text-xl font-black text-emerald-700 dark:text-emerald-300 mt-0.5">
                {announcements.filter(a => a.isActive).length}
              </p>
            </div>
            <div className="p-3.5 rounded-xl bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-900/40">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 font-mono">Top Banners</span>
              <p className="text-xl font-black text-indigo-700 dark:text-indigo-300 mt-0.5">
                {announcements.filter(a => a.displayMode === 'top_banner' || a.displayMode === 'both').length}
              </p>
            </div>
            <div className="p-3.5 rounded-xl bg-purple-50/50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900/40">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-purple-600 dark:text-purple-400 font-mono">Modal Showcases</span>
              <p className="text-xl font-black text-purple-700 dark:text-purple-300 mt-0.5">
                {announcements.filter(a => a.displayMode === 'modal_showcase' || a.displayMode === 'both').length}
              </p>
            </div>
          </div>

          {/* Search bar */}
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Search announcements by title, message, or tone..."
              className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg pl-9 pr-3 py-2 text-xs text-slate-800 dark:text-slate-200 outline-none focus:border-indigo-500 font-medium"
            />
          </div>

          {/* Announcement items list */}
          {filteredList.length === 0 ? (
            <div className="p-12 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
              <Megaphone className="w-10 h-10 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
              <p className="text-sm font-bold text-slate-600 dark:text-slate-400">No announcements found</p>
              <p className="text-xs text-slate-400 mt-1">Create your first announcement banner to broadcast feature updates.</p>
              <button
                onClick={handleCreateNew}
                className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition cursor-pointer shadow-sm"
              >
                + Create Announcement
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredList.map((ann) => {
                const isExpired = ann.expiresAt && new Date(ann.expiresAt).getTime() < Date.now();
                const toneObj = TONE_OPTIONS.find(t => t.id === ann.tone) || TONE_OPTIONS[0];

                return (
                  <div
                    key={ann.id}
                    className="p-4 rounded-xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-xs hover:border-slate-300 dark:hover:border-slate-700 transition flex flex-col md:flex-row md:items-center justify-between gap-4"
                  >
                    <div className="space-y-1.5 flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        {/* Active toggle indicator */}
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider font-mono border ${
                            ann.isActive
                              ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800'
                              : 'bg-slate-100 dark:bg-slate-900 text-slate-500 border-slate-200 dark:border-slate-800'
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${ann.isActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
                          {ann.isActive ? 'Active' : 'Inactive'}
                        </span>

                        {/* Tone badge */}
                        <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider font-mono border ${toneObj.badgeClass}`}>
                          {ann.tone}
                        </span>

                        {/* Display mode badge */}
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 font-mono">
                          {ann.displayMode === 'both' ? 'Banner + Modal' : ann.displayMode === 'top_banner' ? 'Banner Only' : 'Modal Only'}
                        </span>

                        {/* Expiration badge */}
                        {ann.expiresAt && (
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold font-mono flex items-center gap-1 ${
                            isExpired
                              ? 'bg-red-50 text-red-600 border border-red-200 dark:bg-red-950/40 dark:border-red-900'
                              : 'bg-slate-100 text-slate-500 dark:bg-slate-900 border border-slate-200 dark:border-slate-800'
                          }`}>
                            <Clock className="w-3 h-3" />
                            {isExpired ? 'Expired' : `Expires: ${new Date(ann.expiresAt).toLocaleDateString()}`}
                          </span>
                        )}
                      </div>

                      <h3 className="text-sm font-extrabold text-slate-900 dark:text-slate-100 truncate">
                        {ann.title}
                      </h3>

                      <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-1">
                        {ann.message}
                      </p>

                      {/* Targeting summary */}
                      <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500 dark:text-slate-400 pt-1 font-medium">
                        <span className="flex items-center gap-1">
                          <Target className="w-3.5 h-3.5 text-indigo-500" />
                          Audience: {(!ann.targetRoles?.length && !ann.targetDepartmentNames?.length && !ann.targetUserEmails?.length)
                            ? 'All CRM Users'
                            : [
                                ann.targetRoles?.length ? `Roles (${ann.targetRoles.join(', ')})` : null,
                                ann.targetDepartmentNames?.length ? `Depts (${ann.targetDepartmentNames.join(', ')})` : null,
                                ann.targetUserEmails?.length ? `${ann.targetUserEmails.length} Email(s)` : null,
                              ].filter(Boolean).join(' • ')}
                        </span>

                        {ann.featureItems && ann.featureItems.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                            {ann.featureItems.length} Feature Card{ann.featureItems.length === 1 ? '' : 's'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Quick action buttons */}
                    <div className="flex items-center gap-1.5 shrink-0 self-end md:self-center">
                      {/* Active toggle button */}
                      {onToggleActive && (
                        <button
                          onClick={() => onToggleActive(ann.id, !ann.isActive)}
                          className={`p-2 rounded-lg text-xs font-bold transition cursor-pointer border ${
                            ann.isActive
                              ? 'bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800'
                              : 'bg-emerald-50 dark:bg-emerald-950/30 hover:bg-emerald-100 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900'
                          }`}
                          title={ann.isActive ? 'Deactivate Announcement' : 'Activate Announcement'}
                        >
                          {ann.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      )}

                      <button
                        onClick={() => handleEdit(ann)}
                        className="p-2 bg-slate-100 dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 hover:text-indigo-600 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-bold transition cursor-pointer border border-slate-200 dark:border-slate-800"
                        title="Edit Announcement"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => handleDuplicate(ann)}
                        className="p-2 bg-slate-100 dark:bg-slate-900 hover:bg-blue-50 dark:hover:bg-blue-950/40 hover:text-blue-600 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-bold transition cursor-pointer border border-slate-200 dark:border-slate-800"
                        title="Duplicate Announcement"
                      >
                        <Copy className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => {
                          if (confirm(`Are you sure you want to delete "${ann.title}"?`)) {
                            onDelete(ann.id);
                          }
                        }}
                        className="p-2 bg-slate-100 dark:bg-slate-900 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-600 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-bold transition cursor-pointer border border-slate-200 dark:border-slate-800"
                        title="Delete Announcement"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      )}

      {/* ========================================================================= */}
      {/* 2. EDITOR / BUILDER & LIVE PREVIEW VIEW */}
      {/* ========================================================================= */}
      {activeView === 'editor' && editingBanner && (
        <div className="space-y-6">
          
          {/* Notifications */}
          {saveError && (
            <div className="p-3 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 rounded-lg text-xs border border-red-200 dark:border-red-900 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{saveError}</span>
            </div>
          )}

          {saveSuccess && (
            <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 rounded-lg text-xs border border-emerald-200 dark:border-emerald-900 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 shrink-0" />
              <span>{saveSuccess}</span>
            </div>
          )}

          {/* LIVE PREVIEW PANE AT TOP */}
          <div className="bg-slate-900 text-white rounded-2xl p-4 shadow-lg border border-slate-800 space-y-3">
            <div className="flex items-center justify-between border-b border-slate-800 pb-2">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-indigo-400" />
                <span className="text-xs font-extrabold tracking-wider uppercase font-mono text-indigo-300">
                  Live Real-Time Preview
                </span>
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setPreviewTab('banner')}
                  className={`px-3 py-1 rounded-md text-xs font-bold transition cursor-pointer ${
                    previewTab === 'banner'
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'bg-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  Top Banner Preview
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPreviewTab('modal');
                    setIsLiveModalPreviewOpen(true);
                  }}
                  className={`px-3 py-1 rounded-md text-xs font-bold transition cursor-pointer flex items-center gap-1 ${
                    previewTab === 'modal'
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'bg-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  <span>Modal Preview</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Render Live Banner Preview */}
            <div className="rounded-xl overflow-hidden border border-slate-800/80 shadow-md">
              <BannerBar
                announcements={[editingBanner]}
                onDismiss={() => {}}
                onOpenModal={() => setIsLiveModalPreviewOpen(true)}
              />
            </div>
          </div>

          {/* EDITING FORM */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Left 2 Cols: Main Info & Feature Highlight Items */}
            <div className="lg:col-span-2 space-y-5">
              
              {/* General Details Card */}
              <div className="bg-white dark:bg-slate-950 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4 shadow-xs">
                <h3 className="text-xs font-extrabold text-slate-900 dark:text-slate-100 uppercase tracking-wider flex items-center gap-2">
                  <Layout className="w-4 h-4 text-indigo-600" />
                  Announcement Content & Messaging
                </h3>

                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                    Announcement Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingBanner.title}
                    onChange={(e) => setEditingBanner({ ...editingBanner, title: e.target.value })}
                    placeholder="e.g., ✨ Welcome to Armellini CRM 2.0 Feature Spotlight!"
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-900 dark:text-slate-100 outline-none focus:border-indigo-500 font-bold"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                    Summary Message / Subtitle <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={2}
                    value={editingBanner.message}
                    onChange={(e) => setEditingBanner({ ...editingBanner, message: e.target.value })}
                    placeholder="Brief explanation shown on the banner and modal header..."
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-900 dark:text-slate-100 outline-none focus:border-indigo-500 font-normal leading-relaxed"
                  />
                </div>

                {/* Tone Palette Selection */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5 flex items-center gap-1.5">
                    <Palette className="w-3.5 h-3.5 text-indigo-500" />
                    Tone & Visual Palette
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {TONE_OPTIONS.map((t) => (
                      <button
                        type="button"
                        key={t.id}
                        onClick={() => setEditingBanner({ ...editingBanner, tone: t.id })}
                        className={`p-2.5 rounded-xl border text-left transition cursor-pointer flex flex-col justify-between ${
                          editingBanner.tone === t.id
                            ? 'bg-indigo-50/50 dark:bg-indigo-950/40 border-indigo-600 ring-2 ring-indigo-500/20 shadow-xs'
                            : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300'
                        }`}
                      >
                        <span className={`text-[10px] font-black uppercase font-mono px-1.5 py-0.5 rounded border inline-block ${t.badgeClass}`}>
                          {t.id}
                        </span>
                        <span className="text-[10px] font-bold text-slate-700 dark:text-slate-300 mt-2 truncate">
                          {t.label.split(' ')[0]}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Display Mode Selection */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                    Display Mode
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {DISPLAY_MODES.map((mode) => (
                      <button
                        type="button"
                        key={mode.id}
                        onClick={() => setEditingBanner({ ...editingBanner, displayMode: mode.id })}
                        className={`p-2.5 rounded-xl border text-left transition cursor-pointer ${
                          editingBanner.displayMode === mode.id
                            ? 'bg-indigo-50/50 dark:bg-indigo-950/40 border-indigo-600 ring-2 ring-indigo-500/20 shadow-xs'
                            : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300'
                        }`}
                      >
                        <p className="text-xs font-extrabold text-slate-900 dark:text-slate-100">{mode.label}</p>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{mode.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* CTA Settings */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                      CTA Button Label
                    </label>
                    <input
                      type="text"
                      value={editingBanner.ctaLabel || ''}
                      onChange={(e) => setEditingBanner({ ...editingBanner, ctaLabel: e.target.value })}
                      placeholder="e.g. See What's New"
                      className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-900 dark:text-slate-100 outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                      CTA Action
                    </label>
                    <select
                      value={editingBanner.ctaAction || 'open_modal'}
                      onChange={(e) => setEditingBanner({ ...editingBanner, ctaAction: e.target.value })}
                      className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-900 dark:text-slate-100 outline-none focus:border-indigo-500 font-bold"
                    >
                      <option value="open_modal">Open Feature Showcase Modal</option>
                      <option value="navigate_url">Navigate to Tab / URL</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                      Target Tab / URL (optional)
                    </label>
                    <input
                      type="text"
                      value={editingBanner.ctaUrl || ''}
                      onChange={(e) => setEditingBanner({ ...editingBanner, ctaUrl: e.target.value })}
                      placeholder="e.g. dashboard or https://..."
                      className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-900 dark:text-slate-100 outline-none focus:border-indigo-500 font-mono"
                    />
                  </div>
                </div>

              </div>

              {/* Feature Highlights Item Builder */}
              <div className="bg-white dark:bg-slate-950 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4 shadow-xs">
                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                  <div>
                    <h3 className="text-xs font-extrabold text-slate-900 dark:text-slate-100 uppercase tracking-wider flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      Feature Highlights Cards Builder
                    </h3>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                      Add and curate spotlight feature cards displayed inside the "What's New" modal.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleAddFeatureItem}
                    className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-bold transition cursor-pointer flex items-center gap-1 border border-indigo-200 dark:border-indigo-900"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add Card</span>
                  </button>
                </div>

                {(!editingBanner.featureItems || editingBanner.featureItems.length === 0) ? (
                  <div className="p-8 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
                    <p className="text-xs text-slate-500">No feature cards added yet.</p>
                    <button
                      type="button"
                      onClick={handleAddFeatureItem}
                      className="mt-2 text-xs font-bold text-indigo-600 hover:underline cursor-pointer"
                    >
                      + Add first feature highlight
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {editingBanner.featureItems.map((item, idx) => (
                      <div
                        key={item.id || idx}
                        className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30 space-y-3"
                      >
                        {/* Header: Icon, Badge, Reorder, Delete */}
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-mono font-bold text-slate-400">
                              #{idx + 1}
                            </span>
                            {/* Visual Icon Picker */}
                            <IconPicker
                              selectedIcon={item.iconName}
                              onSelect={(name) => handleUpdateFeatureItem(idx, { iconName: name })}
                            />
                            {/* Badge Tag Selector */}
                            <select
                              value={item.badge || 'NEW'}
                              onChange={(e) => handleUpdateFeatureItem(idx, { badge: e.target.value })}
                              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2.5 py-1.5 text-xs font-black text-slate-800 dark:text-slate-200 outline-none font-mono"
                            >
                              <option value="NEW">NEW</option>
                              <option value="IMPROVED">IMPROVED</option>
                              <option value="PRO TIP">PRO TIP</option>
                              <option value="BETA">BETA</option>
                              <option value="IMPORTANT">IMPORTANT</option>
                            </select>
                          </div>

                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleMoveFeatureItem(idx, 'up')}
                              disabled={idx === 0}
                              className="p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 disabled:opacity-30 cursor-pointer"
                              title="Move Up"
                            >
                              <ArrowUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveFeatureItem(idx, 'down')}
                              disabled={idx === (editingBanner.featureItems?.length || 0) - 1}
                              className="p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 disabled:opacity-30 cursor-pointer"
                              title="Move Down"
                            >
                              <ArrowDown className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteFeatureItem(idx)}
                              className="p-1 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 cursor-pointer ml-1"
                              title="Delete Card"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        {/* Card Title and Description */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                              Card Title
                            </label>
                            <input
                              type="text"
                              value={item.title}
                              onChange={(e) => handleUpdateFeatureItem(idx, { title: e.target.value })}
                              placeholder="e.g. Quoting Matrix Engine"
                              className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2.5 py-1.5 text-xs text-slate-900 dark:text-slate-100 outline-none focus:border-indigo-500 font-bold"
                            />
                          </div>

                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                              Action Button Label (optional)
                            </label>
                            <input
                              type="text"
                              value={item.actionLabel || ''}
                              onChange={(e) => handleUpdateFeatureItem(idx, { actionLabel: e.target.value })}
                              placeholder="e.g. Open CRM Dashboard"
                              className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2.5 py-1.5 text-xs text-slate-900 dark:text-slate-100 outline-none focus:border-indigo-500"
                            />
                          </div>

                          <div className="sm:col-span-2">
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                              Card Description
                            </label>
                            <textarea
                              rows={2}
                              value={item.description}
                              onChange={(e) => handleUpdateFeatureItem(idx, { description: e.target.value })}
                              placeholder="Explain what was launched or improved and how it helps the user..."
                              className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2.5 py-1.5 text-xs text-slate-900 dark:text-slate-100 outline-none focus:border-indigo-500 leading-relaxed font-normal"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>

            {/* Right 1 Col: Audience Targeting, Expiration & Settings */}
            <div className="space-y-5">
              
              {/* Audience Targeting Card */}
              <div className="bg-white dark:bg-slate-950 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4 shadow-xs">
                <h3 className="text-xs font-extrabold text-slate-900 dark:text-slate-100 uppercase tracking-wider flex items-center gap-2">
                  <Target className="w-4 h-4 text-indigo-600" />
                  Audience Targeting
                </h3>

                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-xs font-bold text-slate-800 dark:text-slate-200 cursor-pointer">
                    <input
                      type="radio"
                      name="targetingMode"
                      checked={targetingMode === 'all'}
                      onChange={() => setTargetingMode('all')}
                      className="text-indigo-600"
                    />
                    <span>All CRM Users (Broadcast)</span>
                  </label>

                  <label className="flex items-center gap-2 text-xs font-bold text-slate-800 dark:text-slate-200 cursor-pointer">
                    <input
                      type="radio"
                      name="targetingMode"
                      checked={targetingMode === 'custom'}
                      onChange={() => setTargetingMode('custom')}
                      className="text-indigo-600"
                    />
                    <span>Targeted Audience (Roles / Depts / Emails)</span>
                  </label>
                </div>

                {targetingMode === 'custom' && (
                  <div className="space-y-4 pt-3 border-t border-slate-100 dark:border-slate-800 animate-in fade-in duration-100">
                    
                    {/* Role Targeting */}
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 mb-1.5">
                        Target User Roles
                      </label>
                      <div className="space-y-1.5">
                        {ALL_ROLES.map((r) => {
                          const isChecked = (editingBanner.targetRoles || []).includes(r.id);
                          return (
                            <label
                              key={r.id}
                              className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  const current = editingBanner.targetRoles || [];
                                  const updated = e.target.checked
                                    ? [...current, r.id]
                                    : current.filter(id => id !== r.id);
                                  setEditingBanner({ ...editingBanner, targetRoles: updated });
                                }}
                                className="rounded text-indigo-600"
                              />
                              <span>{r.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Department Targeting */}
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 mb-1.5">
                        Target Departments
                      </label>
                      <div className="space-y-1.5">
                        {STANDARD_DEPARTMENTS.map((dept) => {
                          const isChecked = (editingBanner.targetDepartmentNames || []).includes(dept);
                          return (
                            <label
                              key={dept}
                              className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  const current = editingBanner.targetDepartmentNames || [];
                                  const updated = e.target.checked
                                    ? [...current, dept]
                                    : current.filter(d => d !== dept);
                                  setEditingBanner({ ...editingBanner, targetDepartmentNames: updated });
                                }}
                                className="rounded text-indigo-600"
                              />
                              <span>{dept}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Specific Emails Whitelist */}
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 mb-1">
                        Specific User Emails Whitelist
                      </label>
                      <div className="flex gap-1.5">
                        <input
                          type="email"
                          value={emailInput}
                          onChange={(e) => setEmailInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddEmailTag();
                            }
                          }}
                          placeholder="user@armellini.com"
                          className="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2.5 py-1 text-xs outline-none focus:border-indigo-500 font-mono"
                        />
                        <button
                          type="button"
                          onClick={handleAddEmailTag}
                          className="px-2.5 py-1 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 text-xs font-bold rounded-lg cursor-pointer"
                        >
                          Add
                        </button>
                      </div>

                      {editingBanner.targetUserEmails && editingBanner.targetUserEmails.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {editingBanner.targetUserEmails.map((email) => (
                            <span
                              key={email}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 text-[10px] font-mono font-bold border border-indigo-200 dark:border-indigo-900"
                            >
                              <span>{email}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveEmailTag(email)}
                                className="hover:text-red-500 cursor-pointer"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                  </div>
                )}
              </div>

              {/* Status & Expiration Settings Card */}
              <div className="bg-white dark:bg-slate-950 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4 shadow-xs">
                <h3 className="text-xs font-extrabold text-slate-900 dark:text-slate-100 uppercase tracking-wider flex items-center gap-2">
                  <Sliders className="w-4 h-4 text-indigo-600" />
                  Status & Expiration
                </h3>

                {/* Active Switch */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs font-bold text-slate-800 dark:text-slate-200">Active Status</span>
                    <p className="text-[10px] text-slate-500">Enable or disable banner broadcasting</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={editingBanner.isActive}
                    onChange={(e) => setEditingBanner({ ...editingBanner, isActive: e.target.checked })}
                    className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                  />
                </div>

                {/* Dismissible Toggle */}
                <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
                  <div>
                    <span className="text-xs font-bold text-slate-800 dark:text-slate-200">Dismissible</span>
                    <p className="text-[10px] text-slate-500">Allow users to close and hide this banner</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={editingBanner.dismissible !== false}
                    onChange={(e) => setEditingBanner({ ...editingBanner, dismissible: e.target.checked })}
                    className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                  />
                </div>

                {/* Expiration Date-Time Picker */}
                <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                  <label className="block text-xs font-bold text-slate-800 dark:text-slate-200 mb-1 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    Expiration Date & Time (optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={editingBanner.expiresAt ? editingBanner.expiresAt.substring(0, 16) : ''}
                    onChange={(e) => setEditingBanner({ ...editingBanner, expiresAt: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 outline-none focus:border-indigo-500 font-mono"
                  />
                  {editingBanner.expiresAt && (
                    <button
                      type="button"
                      onClick={() => setEditingBanner({ ...editingBanner, expiresAt: undefined })}
                      className="mt-1 text-[10px] text-slate-400 hover:text-red-500 font-mono cursor-pointer"
                    >
                      Clear Expiration (Never expires)
                    </button>
                  )}
                </div>

              </div>

              {/* Action Buttons: Save / Cancel */}
              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleSaveEditor}
                  disabled={isSaving}
                  className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-xl text-xs font-extrabold transition cursor-pointer shadow-md flex items-center justify-center gap-2 active:scale-[0.98]"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  <span>{isSaving ? 'Saving Announcement...' : 'Save Announcement'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveView('list')}
                  className="px-4 py-2.5 bg-slate-100 dark:bg-slate-900 hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition cursor-pointer border border-slate-200 dark:border-slate-800"
                >
                  Cancel
                </button>
              </div>

            </div>

          </div>

          {/* Render Modal Preview in a popup if toggled */}
          {isLiveModalPreviewOpen && (
            <FeatureShowcaseModal
              isOpen={isLiveModalPreviewOpen}
              onClose={() => setIsLiveModalPreviewOpen(false)}
              announcement={editingBanner}
              allAnnouncements={[editingBanner]}
            />
          )}

        </div>
      )}

    </div>
  );
}
